import { useEffect, useRef } from 'react';
import { initGridDistortion } from '../lib/gridDistortion.js';
import { ResponsivePicture } from './ResponsivePicture.jsx';

/**
 * Mouse-driven WebGL grid distortion over a full-bleed image (heroes, footers).
 *
 * The <picture> underneath is always rendered: it is what reduced-motion,
 * touch-only and no-WebGL visitors see, and it is what the canvas fades in
 * over once the texture is ready (`.has-distortion img { opacity: 0 }`).
 *
 *  - eager: true for above-the-fold heroes (fetchpriority=high, no lazy
 *    loading); false for footers so the image — and the WebGL context that
 *    waits for it — only spin up once the footer approaches the viewport.
 */
export function GridDistortion({
  src,
  alt = '',
  heroRef,
  grid = 12,
  mouse = 0.08,
  strength = 0.1,
  relaxation = 0.92,
  className = 'hero-bg',
  eager = true,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The effect is driven purely by mouse and pen movement. On touch-only
    // devices it can never be triggered, so the canvas would just be a
    // permanently running render loop on top of an identical-looking image —
    // skip it there, the same way the cursor glow and magnetic hover already do.
    const hoverCapable = window.matchMedia('(hover: hover)').matches;
    if (prefersReducedMotion || !hoverCapable) return undefined;
    const el = ref.current;
    if (!el || !src) return undefined;

    const img = el.querySelector('img');
    let destroy = null;
    let cancelled = false;

    // Build the texture from the URL the <picture> actually chose (AVIF/WebP
    // at a width that fits this viewport) so the browser downloads the hero
    // exactly once — the texture request is then served from cache instead
    // of fetching the full-size JPG a second time.
    const mount = () => {
      if (cancelled) return;
      destroy = initGridDistortion(el, {
        imageSrc: (img && img.currentSrc) || src,
        eventTarget: heroRef?.current || el,
        grid,
        mouse,
        strength,
        relaxation,
      });
    };
    if (!img || img.complete) {
      mount();
    } else {
      img.addEventListener('load', mount, { once: true });
      img.addEventListener('error', mount, { once: true });
    }

    return () => {
      cancelled = true;
      img?.removeEventListener('load', mount);
      img?.removeEventListener('error', mount);
      destroy?.();
    };
  }, [src, heroRef, grid, mouse, strength, relaxation]);

  return (
    <div ref={ref} className={className} data-grid-distortion="" data-image-src={src}>
      <ResponsivePicture src={src} alt={alt} sizes="100vw" eager={eager} />
    </div>
  );
}
